import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Plus, Pencil, Trash2, X, Building2, Search, Upload, Check, AlertCircle, Loader2, Eye
} from 'lucide-react';
import axios from 'axios';
import { detectPerms } from '../../permissions';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const initialFormData = {
  company_name: '',
  email: '',
  phone: '',
  gst_number: '',
  country: 'India',
  state: '',
  city: '',
  address: '',
  area: '',
  pincode: '',
  active: true,
  logo: null
};

const indianStates = [
  'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh',
  'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka',
  'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram',
  'Nagaland', 'Odisha', 'Punjab', 'Rajasthan', 'Sikkim', 'Tamil Nadu',
  'Telangana', 'Tripura', 'Uttar Pradesh', 'Uttarakhand', 'West Bengal',
  'Delhi', 'Jammu and Kashmir', 'Ladakh'
];

export const Companies = () => {
  const navigate = useNavigate();
  const perms = detectPerms();
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);
  const [formData, setFormData] = useState(initialFormData);
  const [searchQuery, setSearchQuery] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
  });

  useEffect(() => {
    fetchCompanies();
  }, []);

  const fetchCompanies = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${BACKEND_URL}/api/companies`, getAuthHeaders());
      setCompanies(response.data);
    } catch (err) {
      console.error('Error fetching companies:', err);
      setError('Failed to fetch companies');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) {
        setError('Logo file size must be less than 2MB');
        return;
      }
      if (!['image/png', 'image/jpeg', 'image/jpg'].includes(file.type)) {
        setError('Logo must be PNG or JPG format');
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData(prev => ({ ...prev, logo: reader.result }));
      };
      reader.readAsDataURL(file);
    }
  };

  const openAddModal = () => {
    setFormData(initialFormData);
    setEditingCompany(null);
    setError('');
    setShowModal(true);
  };

  const openEditModal = (company) => {
    setFormData({
      company_name: company.company_name || '',
      email: company.email || '',
      phone: company.phone || '',
      gst_number: company.gst_number || '',
      country: company.country || 'India',
      state: company.state || '',
      city: company.city || '',
      address: company.address || '',
      area: company.area || '',
      pincode: company.pincode || '',
      active: company.active ?? true,
      logo: company.logo || null
    });
    setEditingCompany(company);
    setError('');
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!formData.company_name.trim()) {
      setError('Company name is required');
      return;
    }

    setSubmitting(true);
    try {
      if (editingCompany) {
        await axios.put(
          `${BACKEND_URL}/api/companies/${editingCompany.id}`,
          formData,
          getAuthHeaders()
        );
      } else {
        await axios.post(
          `${BACKEND_URL}/api/companies`,
          formData,
          getAuthHeaders()
        );
      }
      setShowModal(false);
      fetchCompanies();
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (Array.isArray(detail)) {
        setError(detail.map(e => e.msg).join(', '));
      } else {
        setError(detail || 'Failed to save company');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (companyId) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/companies/${companyId}`, getAuthHeaders());
      setDeleteConfirm(null);
      fetchCompanies();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to delete company');
    }
  };

  const filteredCompanies = companies.filter(c =>
    c.company_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    c.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    c.city?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div data-testid="companies-page">
      {/* Page Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 uppercase tracking-tight">Companies</h1>
          <p className="text-slate-600 mt-1">Manage your company records</p>
        </div>
        {perms.canAdd && (
        <button
          onClick={openAddModal}
          data-testid="add-company-btn"
          className="inline-flex items-center gap-2 bg-orange-500 text-white font-bold tracking-wider uppercase px-6 py-3 rounded-none hover:bg-orange-600 transition-colors"
        >
          <Plus className="h-5 w-5" />
          Add Company
        </button>
        )}
      </div>

      {/* Search Bar */}
      <div className="bg-white rounded-sm border border-slate-200 p-4 mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
          <input
            type="text"
            placeholder="Search companies..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            data-testid="search-input"
            className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none transition-colors"
          />
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-sm mb-6">
          <AlertCircle className="h-5 w-5" />
          <span>{error}</span>
          <button onClick={() => setError('')} className="ml-auto">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Companies Table */}
      <div className="bg-white rounded-sm border border-slate-200">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-orange-500" />
          </div>
        ) : filteredCompanies.length === 0 ? (
          <div className="text-center py-16" data-testid="empty-state">
            <Building2 className="h-16 w-16 mx-auto mb-4 text-slate-300" />
            <h3 className="text-xl font-semibold text-slate-700 mb-2">No companies yet</h3>
            <p className="text-slate-500 mb-6">Start by adding your first company</p>
            <button
              onClick={openAddModal}
              data-testid="empty-add-btn"
              className="inline-flex items-center gap-2 bg-orange-500 text-white font-bold tracking-wider uppercase px-6 py-3 rounded-none hover:bg-orange-600 transition-colors"
            >
              <Plus className="h-5 w-5" />
              Add First Company
            </button>
          </div>
        ) : (
          <>
            {/* Table Header */}
            <div className="hidden md:grid grid-cols-7 gap-4 px-6 py-4 border-b border-slate-200 bg-slate-50">
              <div className="font-semibold text-slate-700 text-sm uppercase tracking-wider">Logo</div>
              <div className="font-semibold text-slate-700 text-sm uppercase tracking-wider col-span-2">Company Name</div>
              <div className="font-semibold text-slate-700 text-sm uppercase tracking-wider">Email</div>
              <div className="font-semibold text-slate-700 text-sm uppercase tracking-wider">Phone</div>
              <div className="font-semibold text-slate-700 text-sm uppercase tracking-wider">Status</div>
              <div className="font-semibold text-slate-700 text-sm uppercase tracking-wider">Actions</div>
            </div>

            {/* Table Rows */}
            {filteredCompanies.map((company) => (
              <div
                key={company.id}
                data-testid={`company-row-${company.id}`}
                className="grid grid-cols-1 md:grid-cols-7 gap-4 px-6 py-4 border-b border-slate-100 hover:bg-slate-50 items-center"
              >
                <div>
                  {company.logo ? (
                    <img
                      src={company.logo}
                      alt={company.company_name}
                      className="w-10 h-10 object-contain rounded"
                    />
                  ) : (
                    <div className="w-10 h-10 bg-slate-100 rounded flex items-center justify-center">
                      <Building2 className="h-5 w-5 text-slate-400" />
                    </div>
                  )}
                </div>
                <div className="col-span-2">
                  <div className="font-medium text-slate-900">{company.company_name}</div>
                  <div className="text-sm text-slate-500">{company.city}, {company.state}</div>
                </div>
                <div className="text-slate-600 text-sm truncate">{company.email || '-'}</div>
                <div className="text-slate-600 text-sm">{company.phone || '-'}</div>
                <div>
                  <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${
                    company.active 
                      ? 'bg-green-100 text-green-700' 
                      : 'bg-red-100 text-red-700'
                  }`}>
                    {company.active ? <Check className="h-3 w-3" /> : <X className="h-3 w-3" />}
                    {company.active ? 'Active' : 'Inactive'}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => navigate(`/admin/master-data/companies/${company.id}`)}
                    data-testid={`view-btn-${company.id}`}
                    className="p-2 text-slate-600 hover:text-orange-500 hover:bg-orange-50 rounded transition-colors"
                    title="View Details"
                  >
                    <Eye className="h-4 w-4" />
                  </button>
                  {perms.canEdit && (
                  <button
                    onClick={() => openEditModal(company)}
                    data-testid={`edit-btn-${company.id}`}
                    className="p-2 text-slate-600 hover:text-orange-500 hover:bg-orange-50 rounded transition-colors"
                    title="Edit"
                  >
                    <Pencil className="h-4 w-4" />
                  </button>
                  )}
                  <button
                    onClick={() => setDeleteConfirm(company.id)}
                    data-testid={`delete-btn-${company.id}`}
                    className="p-2 text-slate-600 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                    title="Delete"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-sm p-6 max-w-md w-full">
            <h3 className="text-xl font-bold text-slate-900 mb-4">Confirm Delete</h3>
            <p className="text-slate-600 mb-6">Are you sure you want to delete this company? This action cannot be undone.</p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="px-4 py-2 border border-slate-200 text-slate-700 rounded-sm hover:bg-slate-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(deleteConfirm)}
                data-testid="confirm-delete-btn"
                className="px-4 py-2 bg-red-500 text-white rounded-sm hover:bg-red-600 transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-sm w-full max-w-3xl my-8">
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
              <h2 className="text-xl font-bold text-slate-900 uppercase tracking-tight">
                {editingCompany ? 'Edit Company' : 'Add New Company'}
              </h2>
              <button
                onClick={() => setShowModal(false)}
                className="p-2 text-slate-400 hover:text-slate-600 transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Modal Body */}
            <form onSubmit={handleSubmit} className="p-6 max-h-[70vh] overflow-y-auto">
              {error && (
                <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-sm mb-6">
                  <AlertCircle className="h-5 w-5" />
                  <span>{error}</span>
                </div>
              )}

              {/* Basic Information */}
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-slate-800 mb-4 border-b pb-2">Basic Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Company Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="company_name"
                      value={formData.company_name}
                      onChange={handleInputChange}
                      data-testid="input-company-name"
                      className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      data-testid="input-email"
                      className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Phone</label>
                    <input
                      type="text"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      data-testid="input-phone"
                      className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">GST Number</label>
                    <input
                      type="text"
                      name="gst_number"
                      value={formData.gst_number}
                      onChange={handleInputChange}
                      data-testid="input-gst"
                      className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none"
                    />
                  </div>
                </div>
              </div>

              {/* Address Information */}
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-slate-800 mb-4 border-b pb-2">Address Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Country</label>
                    <input
                      type="text"
                      name="country"
                      value={formData.country}
                      onChange={handleInputChange}
                      data-testid="input-country"
                      className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">State</label>
                    <select
                      name="state"
                      value={formData.state}
                      onChange={handleInputChange}
                      data-testid="input-state"
                      className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none"
                    >
                      <option value="">Select State</option>
                      {indianStates.map(state => (
                        <option key={state} value={state}>{state}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">City</label>
                    <input
                      type="text"
                      name="city"
                      value={formData.city}
                      onChange={handleInputChange}
                      data-testid="input-city"
                      className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Area</label>
                    <input
                      type="text"
                      name="area"
                      value={formData.area}
                      onChange={handleInputChange}
                      data-testid="input-area"
                      className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-slate-700 mb-1">Address</label>
                    <textarea
                      name="address"
                      value={formData.address}
                      onChange={handleInputChange}
                      rows={2}
                      data-testid="input-address"
                      className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Pincode</label>
                    <input
                      type="text"
                      name="pincode"
                      value={formData.pincode}
                      onChange={handleInputChange}
                      data-testid="input-pincode"
                      className="w-full px-3 py-2 border border-slate-200 rounded-sm focus:border-orange-500 focus:outline-none"
                    />
                  </div>
                </div>
              </div>

              {/* Status & Logo */}
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-slate-800 mb-4 border-b pb-2">Status & Logo</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      id="active"
                      name="active"
                      checked={formData.active}
                      onChange={handleInputChange}
                      data-testid="input-active"
                      className="w-5 h-5 text-orange-500 border-slate-300 rounded focus:ring-orange-500"
                    />
                    <label htmlFor="active" className="text-sm font-medium text-slate-700">
                      Active
                    </label>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Company Logo (PNG/JPG, &lt;2MB)
                    </label>
                    <div className="flex items-center gap-4">
                      {formData.logo && (
                        <img
                          src={formData.logo}
                          alt="Logo preview"
                          className="w-16 h-16 object-contain border rounded"
                        />
                      )}
                      <label className="cursor-pointer inline-flex items-center gap-2 px-4 py-2 border border-slate-200 rounded-sm hover:bg-slate-50 transition-colors">
                        <Upload className="h-4 w-4" />
                        <span>Choose Logo</span>
                        <input
                          type="file"
                          accept="image/png,image/jpeg,image/jpg"
                          onChange={handleLogoChange}
                          className="hidden"
                          data-testid="input-logo"
                        />
                      </label>
                    </div>
                  </div>
                </div>
              </div>

              {/* Form Actions */}
              <div className="flex gap-3 justify-end pt-4 border-t border-slate-200">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-6 py-2 border border-slate-200 text-slate-700 rounded-sm hover:bg-slate-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="submit-btn"
                  className="px-6 py-2 bg-orange-500 text-white font-bold uppercase tracking-wider rounded-sm hover:bg-orange-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
                >
                  {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
                  {editingCompany ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
